<template>
  <TablePage :layout-mode="layoutMode">
    <template #search>
      <SearchRenderer
        v-if="Array.isArray(searchSchema) ? searchSchema.length > 0 : Object.keys(searchSchema || {}).length > 0"
        :query="query"
        :schema="searchSchema"
        :expanded.sync="expanded"
        :form-props="formProps"
        @search="handleSearch"
        @reset="handleReset"
      >
        <template #extra="slotProps">
          <slot name="search-extra" v-bind="slotProps" />
        </template>
        <template v-for="name in searchSlotNames" #[name]="slotProps">
          <slot :name="name" v-bind="slotProps" />
        </template>
      </SearchRenderer>
    </template>

    <template #table-header>
      <slot name="table-header" />
    </template>

    <template #table-content="{ tableProps }">
      <slot name="table-content" :tableProps="tableProps" :tableData="tableData" :loading="loading" :query="query">
      </slot>
      <el-table
        v-if="columns.length > 0"
        v-loading="loading"
        :data="tableData"
        v-bind="{ ...tableProps, ...tableAttrs }"
        style="width: 100%"
        v-on="tableListeners"
      >
        <template v-for="(col, idx) in columns">
          <el-table-column
            v-if="col && col.slot && !SPECIAL_COLUMN_TYPE.includes(col.type)"
            :key="`${col.key || col.prop || col.type || col.label || idx}-slot`"
            :align="col.align || 'center'"
            v-bind="columnProps(col)"
          >
            <template slot-scope="scope">
              <slot :name="col.slot" v-bind="scope" />
            </template>
          </el-table-column>
          <el-table-column
            v-else
            :key="`${
              col && (col.key || col.prop || col.type || col.label) ? col.key || col.prop || col.type || col.label : idx
            }-plain`"
            :align="col.align || 'center'"
            v-bind="columnProps(col)"
          >
          </el-table-column>
        </template>
      </el-table>
      <slot name="table-after" :query="query" />
    </template>

    <template #pagination>
      <Pagination :total="total" :page.sync="page" :limit.sync="limit" @pagination="handlePagination($event)" />
    </template>
  </TablePage>
</template>

<script>
import { computed, getCurrentInstance, ref } from 'vue'
import TablePage from '@/layout/TablePage.vue'
import Pagination from '@/components/Pagination/index.vue'
import useTableQuery from '@/composables/useTableQuery'
import SearchRenderer from './SearchRenderer.vue'

export default {
  name: 'SchemaTablePage',
  components: { TablePage, Pagination, SearchRenderer },
  props: {
    searchSchema: {
      type: Array,
      default: () => [],
    },
    formProps: {
      type: Object,
      default: () => ({}),
    },
    columns: {
      type: Array,
      default: () => [],
    },
    defaultQuery: {
      type: Object,
      default: () => ({}),
    },
    request: {
      type: Function,
      required: true,
    },
    buildParams: {
      type: Function,
      required: true,
    },
    parseResult: {
      type: Function,
      default: undefined,
    },
    layoutMode: {
      type: String,
      default: 'page',
    },
    showSummary: {
      type: Boolean,
      default: false,
    },
    summaryMethod: {
      type: Function,
      default: undefined,
    },
    immediate: {
      type: Boolean,
      default: true,
    },
    tableAttrs: {
      type: Object,
      default: () => ({}),
    },
    tableListeners: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const SPECIAL_COLUMN_TYPE = ['index', 'selection']

    // console.log('props.$slots', $slots)
    const { proxy } = getCurrentInstance() || {}
    const expanded = ref(false)

    const { query, tableData, total, loading, page, limit, reload, search, reset, onPagination } = useTableQuery({
      defaultQuery: props.defaultQuery,
      immediate: props.immediate,
      buildParams: props.buildParams,
      request: props.request,
      parseResult: props.parseResult,
    })

    function resolveEmptyValue(value) {
      return value == null || value === '' ? '--' : value
    }

    function toCamelCase(str) {
      if (!str || typeof str !== 'string') return str
      return str.replace(/_([a-zA-Z0-9])/g, (_, c) => String(c).toUpperCase())
    }

    function toSnakeCase(str) {
      if (!str || typeof str !== 'string') return str
      return str.replace(/[A-Z]/g, c => `_${c.toLowerCase()}`)
    }

    function resolveRowValue(row, prop) {
      if (!row || !prop) return undefined
      if (Object.prototype.hasOwnProperty.call(row, prop)) return row[prop]
      const camel = toCamelCase(prop)
      if (camel !== prop && Object.prototype.hasOwnProperty.call(row, camel)) return row[camel]
      const snake = toSnakeCase(prop)
      if (snake !== prop && Object.prototype.hasOwnProperty.call(row, snake)) return row[snake]
      return row[prop] ?? row[camel] ?? row[snake]
    }

    function columnProps(col) {
      if (!col) return {}
      const p = { ...col }
      const userFormatter = p.formatter
      const canUseDefaultFormatter =
        !p.slot && !p.type && p.prop && typeof userFormatter !== 'function' && p.emptyDisplay !== false

      if (canUseDefaultFormatter) {
        p.formatter = (row, column, cellValue) => {
          const prop = p.prop || column?.property
          const value = resolveRowValue(row, prop) ?? cellValue
          return resolveEmptyValue(value)
        }
      }

      delete p.slot
      delete p.key
      delete p.emptyDisplay
      return p
    }

    const columns = computed(() => props.columns || [])

    const columnsWithSlot = computed(() => (props.columns || []).filter(c => c && c.slot))
    const searchSlotNames = computed(() => {
      const slots = proxy?.$scopedSlots || {}
      return Object.keys(slots).filter(n => n && n.startsWith('search-'))
    })

    const handleSearch = () => search()
    const handleReset = () => reset()
    const handlePagination = p => onPagination(p)
    //刷新当前页面
    const refreshCurrentPage = () => {}

    return {
      expanded,
      query,
      tableData,
      total,
      loading,
      page,
      limit,
      reload,
      search,
      reset,
      handleSearch,
      handleReset,
      handlePagination,
      columnProps,
      columnsWithSlot,
      searchSlotNames,
      columns,
      SPECIAL_COLUMN_TYPE,
    }
  },
}
</script>
